import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { apiFetch } from '../../../core/utils/api';
import { useEdit } from '../../context/EditContext';
import ElementTypesList from './ElementTypesList';
import TemplatesList from './TemplatesList';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '../../../design-system/icons';
import { SecondaryButton } from '../../../design-system';
import { typography, surfaces, borders, textColors, interactive } from '../../../design-system/tokens';
import { calculateComponentOrder } from '../../utils/orderCalculation';
import { TUTORIAL_TARGETS } from '../../tutorial/constants';

const TEMPLATE_PREVIEW_LOG_TITLE = 'TEMPLATE_PREVIEW_DEBUG';

// Constants for element categorization
// button_collection is intentionally treated as a simple element:
// selecting it should create immediately (it auto-creates 3 default buttons server-side).
const SIMPLE_ELEMENTS = ['title', 'text', 'button', 'button_collection', 'image', 'video', 'file'];
const TEMPLATE_ELEMENTS = ['component', 'hero'];

// Accept either a normalized target prop or legacy props (componentId, heroId, buttonCollectionId, pageId, order)
const AddElementPanel = ({ target: rawTarget, onElementCreated, onCancel, componentId, heroId, buttonCollectionId, pageId, order }) => {
  // Get websiteId from EditContext
  const { websiteId } = useEdit();
  
  // Build a normalized target object from props to avoid undefined target/order issues.
  // ``restrictToType`` is preserved verbatim — callers (e.g. the hero slot
  // manager) use it to pin the picker to a single element type and to opt
  // into the no-picker "direct creation" flow below.
  const target = React.useMemo(() => {
    const base = rawTarget || {};
    const merged = {
      componentId: base.componentId ?? componentId,
      heroId: base.heroId ?? heroId,
      buttonCollectionId: base.buttonCollectionId ?? buttonCollectionId,
      pageId: base.pageId ?? pageId,
      order: typeof (base.order ?? order) === 'number' ? (base.order ?? order) : 0,
      restrictToType: base.restrictToType,
    };
    return merged;
  }, [rawTarget, componentId, heroId, buttonCollectionId, pageId, order]);

  // When the caller asks for a specific simple element (image/title/text/etc.),
  // skip the picker entirely and create that element directly. This powers the
  // hero slot-manager empty-slot CTA: clicking "Empty · accepts Image" should
  // add an Image on the spot rather than bouncing the user through a single-
  // option element selector. Template elements (hero/component) still need
  // the picker since they require template selection.
  const autoCreateType = React.useMemo(() => {
    const requestedType = target?.restrictToType;
    if (!requestedType) return null;
    return SIMPLE_ELEMENTS.includes(requestedType) ? requestedType : null;
  }, [target?.restrictToType]);

  const [selectedElementType, setSelectedElementType] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [panelWidth, setPanelWidth] = useState(0);
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  // Fires the auto-create path exactly once per mount: the actual creation
  // still runs in ``handleCreateSimpleElement`` (via the element-type effect
  // below), so this flag only guards the seeding of ``selectedElementType``.
  const hasTriggeredAutoCreate = useRef(false);
  const panelRef = useRef(null);

  // Add keyboard shortcut for Escape
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape' && !creating) {
        onCancel();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onCancel, creating]);

  // Measure actual panel width (not window width)
  useEffect(() => {
    if (!panelRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setPanelWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(panelRef.current);
    
    // Set initial width
    setPanelWidth(panelRef.current.offsetWidth);

    return () => resizeObserver.disconnect();
  }, []);

  // Determine if compact mode based on PANEL width (not window width)
  // When an element type is selected and panel is narrow, collapse to icon-only to show more preview space
  const isPanelNarrow = panelWidth < 600; // Panel itself is narrow (not the whole window)
  const isCompact = isPanelNarrow && selectedElementType !== null;
  
  // When panel is narrow and nothing selected, hide right column and show full-width left column
  const shouldHideRightColumn = isPanelNarrow && selectedElementType === null;
  
  // Expand sidebar on hover even when compact
  const shouldExpandOnHover = isCompact && isSidebarHovered;
  
  // Filter available element types based on context
  const availableElementTypes = React.useMemo(() => {
    const hasComponentId = target?.componentId !== undefined;
    const hasHeroId = target?.heroId !== undefined;
    const hasButtonCollectionId = target?.buttonCollectionId !== undefined;
    const hasPageId = target?.pageId !== undefined;
    
    let result;
    let context;
    
    // Adding to ButtonCollection -> only buttons
    if (hasButtonCollectionId) {
      result = ['button'];
      context = 'buttonCollection';
    }
    // Adding to Hero -> all items except components (heroes can't contain nested components)
    else if (hasHeroId) {
      result = ['title', 'text', 'button', 'button_collection', 'image', 'video', 'file'];
      context = 'hero';
    }
    // Adding to Component -> direct items only. Generic components cannot
    // contain nested components anymore; page-level adds still create components.
    else if (hasComponentId) {
      result = ['hero', 'title', 'text', 'button', 'button_collection', 'image', 'video', 'file'];
      context = 'component';
    }
    // Adding to Page -> all items AND components
    else if (hasPageId) {
      result = ['hero', 'title', 'text', 'button', 'button_collection', 'component', 'image', 'video', 'file'];
      context = 'page';
    }
    // Default: show all (fallback, shouldn't happen)
    else {
      result = ['hero', 'title', 'text', 'button', 'button_collection', 'component', 'image', 'video', 'file'];
      context = 'default';
    }
    
    console.log('🔍 ADDELEM_CREATE_ERROR - Available element types filtered:', {
      context,
      target,
      availableTypes: result,
    });
    
    return result;
  }, [target]);

  // Fetch templates for the selected element type
  const fetchTemplates = async (elementType) => {
    if (!elementType || !websiteId) {
      console.warn(`${TEMPLATE_PREVIEW_LOG_TITLE} fetchTemplates skipped`, {
        elementType,
        websiteId,
      });
      return;
    }
    
    console.log(`${TEMPLATE_PREVIEW_LOG_TITLE} fetchTemplates start`, {
      elementType,
      websiteId,
    });
    
    setLoadingTemplates(true);
    setError(null);
    try {
      // First, get the list of templates (metadata only)
      const listResponse = await apiFetch(`/api/templates/items/${elementType}/`);
      if (!listResponse.ok) {
        throw new Error('Failed to fetch templates');
      }
      const templatesList = await listResponse.json();
      
      console.log(`${TEMPLATE_PREVIEW_LOG_TITLE} templates list received`, {
        elementType,
        count: Array.isArray(templatesList) ? templatesList.length : 0,
      });
      
      // Then, fetch the full template details for each to get preview_data
      console.log('[PREVIEW_404] Fetching templates for elementType:', elementType, 'websiteId:', websiteId);
      
      const templatesWithDetails = await Promise.all(
        templatesList.map(async (templateMeta) => {
          try {
            const url = `/api/templates/items/${elementType}/${templateMeta.id}/?website_id=${websiteId}`;
            console.log('[PREVIEW_404] Fetching URL:', url);
            
            const detailResponse = await apiFetch(url);
            console.log('[PREVIEW_404] Response status:', detailResponse.status, 'for template:', templateMeta.id);
            console.log(`${TEMPLATE_PREVIEW_LOG_TITLE} template detail response`, {
              templateId: templateMeta.id,
              status: detailResponse.status,
              ok: detailResponse.ok,
            });
            
            if (detailResponse.ok) {
              const detailData = await detailResponse.json();
              console.log('[PREVIEW_404] Received preview_data:', detailData.preview_data);
              console.log('[PREVIEW_404] preview_data type:', detailData.preview_data?.type);
              console.log('[PREVIEW_404] preview_data id:', detailData.preview_data?.id);
              
              if (!detailData.preview_data) {
                console.warn(`${TEMPLATE_PREVIEW_LOG_TITLE} missing preview_data in detail payload`, {
                  templateId: templateMeta.id,
                  detailKeys: Object.keys(detailData || {}),
                });
              }
              
              return {
                id: detailData.id,
                name: detailData.name,
                description: detailData.description,
                preview_data: detailData.preview_data // Backend sends preview_data field
              };
            }
            console.log('[PREVIEW_404] Response not OK, using metadata fallback');
            console.warn(`${TEMPLATE_PREVIEW_LOG_TITLE} template detail request not ok`, {
              templateId: templateMeta.id,
              status: detailResponse.status,
            });
            return templateMeta; // Fallback to metadata only if detail fetch fails
          } catch (err) {
            console.warn('[PREVIEW_404] Failed to fetch detail for template', templateMeta.id, ':', err);
            console.warn(`${TEMPLATE_PREVIEW_LOG_TITLE} template detail fetch threw`, {
              templateId: templateMeta.id,
              error: err?.message,
            });
            return templateMeta;
          }
        })
      );
      
      console.log('[PREVIEW_404] All templates fetched:', templatesWithDetails.length);
      console.log(`${TEMPLATE_PREVIEW_LOG_TITLE} templates detail merged`, {
        elementType,
        templatesWithPreview: templatesWithDetails.filter(t => !!t.preview_data).length,
      });
      
      setTemplates(templatesWithDetails);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      console.error(`${TEMPLATE_PREVIEW_LOG_TITLE} fetchTemplates failed`, {
        elementType,
        error: err?.message,
      });
      setError('Failed to load templates. Please try again.');
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Seed ``selectedElementType`` from ``restrictToType`` so the shared
  // element-type effect below picks up the creation. We do this via a
  // ``useRef`` latch instead of an effect dependency so re-renders can't
  // re-fire the creation mid-flight (e.g. after setError). On failure the
  // flag stays set — the user can retry from the picker or cancel.
  useEffect(() => {
    if (!autoCreateType) return;
    if (hasTriggeredAutoCreate.current) return;
    hasTriggeredAutoCreate.current = true;
    setSelectedElementType(autoCreateType);
  }, [autoCreateType]);

  // Handle element type selection - determine what to do based on element type
  useEffect(() => {
    if (!selectedElementType) return;
    
    // Simple elements (including image, video, file) create immediately without template selection
    if (SIMPLE_ELEMENTS.includes(selectedElementType)) {
      handleCreateSimpleElement(selectedElementType);
      return;
    }
    
    // Template elements show template selection
    if (TEMPLATE_ELEMENTS.includes(selectedElementType)) {
      fetchTemplates(selectedElementType);
      return;
    }
  }, [selectedElementType, websiteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create a simple element immediately (no template selection)
  const handleCreateSimpleElement = async (elementType) => {
    if (!elementType || !target) return;
    
    console.log('🔍 ADDELEM_CREATE_ERROR - handleCreateSimpleElement called:', {
      elementType,
      target,
      hasOrder: typeof target?.order === 'number',
      orderValue: target?.order,
    });
    
    setCreating(true);
    setError(null);
    
    try {
      let endpoint;
      const payload = {
        type: elementType,
      };
      
      let parentComponentId = null;
      let collectionIdToInvalidate = null;
      
      if (target.buttonCollectionId) {
        // Adding to ButtonCollection - must be a button
        if (elementType !== 'button') {
          throw new Error('Only buttons can be added to button collections');
        }
        
        // Get collection's component
        const collectionRes = await apiFetch(`/api/items/button_collection/${target.buttonCollectionId}/`);
        if (!collectionRes.ok) {
          throw new Error('Failed to fetch button collection information');
        }
        const collection = await collectionRes.json();
        if (!collection.component) {
          throw new Error('ButtonCollection has no component');
        }
        
        parentComponentId = collection.component;
        collectionIdToInvalidate = target.buttonCollectionId;
        
        // Get component's current items to determine proper order
        const componentRes = await apiFetch(`/api/components/${collection.component}/items/`);
        if (!componentRes.ok) {
          throw new Error('Failed to fetch component items');
        }
        const componentItems = await componentRes.json();
        
        // Button should be added at END of component's items, not at the collection order
        payload.order = componentItems.length;
        endpoint = `/api/components/${collection.component}/items/`;
        
        // First, create the button under the component
        const createRes = await apiFetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        
        if (!createRes.ok) {
          const errorData = await createRes.json();
          throw new Error(errorData.error || 'Failed to create button');
        }
        
        const newButton = await createRes.json();
        
        // Second, add button to collection at specified order
        const addRes = await apiFetch(
          `/api/items/button_collection/${target.buttonCollectionId}/add-button/`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              button_id: newButton.id,
              order: target.order ?? 0,
            }),
          }
        );
        
        if (!addRes.ok) {
          throw new Error('Failed to add button to collection');
        }
        
        // Success - pass metadata for soft refresh
        setTimeout(() => {
          setCreating(false);
          onElementCreated({
            ...newButton,
            _refreshMeta: {
              parentComponentId,
              collectionId: collectionIdToInvalidate,
              pageId: target.pageId,
            }
          });
        }, 300);
        return;
        
      } else if (target.heroId) {
        // Adding to hero
        const heroRes = await apiFetch(`/api/items/hero/${target.heroId}/`);
        if (!heroRes.ok) {
          throw new Error('Failed to fetch hero information');
        }
        const hero = await heroRes.json();
        if (!hero.component) {
          throw new Error('Hero has no component');
        }
        parentComponentId = hero.component;
        endpoint = `/api/components/${hero.component}/items/`;
        payload.parent_hero_id = target.heroId;
        payload.order = target.order ?? 0;
        
      } else if (target.componentId) {
        // Adding to component
        parentComponentId = target.componentId;
        endpoint = `/api/components/${target.componentId}/items/`;
        payload.order = target.order ?? 0;
        
        console.log('🔍 ADDELEM_CREATE_ERROR - Adding simple element to component:', {
          componentId: target.componentId,
          elementType,
          orderFromTarget: target.order,
          orderInPayload: payload.order,
          endpoint,
          fullPayload: payload,
        });
        
      } else if (target.pageId) {
        // Adding to page - always create a new component so item appears directly on page
        console.log('🔍 ADDELEM_CREATE_ERROR - Adding simple element to page:', {
          pageId: target.pageId,
          elementType,
          requestedPosition: target.order,
        });
        
        // Step 1: Fetch page to get existing components for order calculation
        const pageResponse = await apiFetch(`/api/websites/${websiteId}/pages/id/${target.pageId}/`);
        if (!pageResponse.ok) {
          throw new Error('Failed to fetch page information');
        }
        const page = await pageResponse.json();
        const existingComponents = page.components || [];
        
        const requestedPosition = target.order ?? 0;
        
        // Step 2: Calculate the optimal component order using fractional insertion
        // This handles all cases: empty page, before first, after last, and between elements
        const componentOrder = calculateComponentOrder(requestedPosition, existingComponents);
        
        console.log('🔍 ADDELEM_CREATE_ERROR - Calculated component order for simple element:', {
          requestedPosition,
          existingComponentsCount: existingComponents.length,
          existingOrders: existingComponents.map(c => c.order ?? 0),
          calculatedComponentOrder: componentOrder,
        });
        
        // Step 3: Create a new component at the calculated order
        const componentPayload = {
          from_default: true,
          parent_website_page_id: target.pageId,
          order: componentOrder,
        };
        
        const componentResponse = await apiFetch(`/api/websites/${websiteId}/components/create/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(componentPayload),
        });
        
        if (!componentResponse.ok) {
          const errorData = await componentResponse.json();
          throw new Error(errorData.error || 'Failed to create component');
        }
        
        const { component: newComponent } = await componentResponse.json();
        parentComponentId = newComponent.id;
        
        console.log('🔍 ADDELEM_CREATE_ERROR - Created new component for page item:', {
          componentId: parentComponentId,
          componentOrder: newComponent.order,
          requestedPosition,
          calculatedOrder: componentOrder,
        });
        
        // Step 4: Add the item to the newly created component
        endpoint = `/api/components/${parentComponentId}/items/`;
        payload.order = 0; // First item in the new component
        
      } else {
        throw new Error('No valid parent specified');
      }
      
      console.log('🔍 ADDELEM_CREATE_ERROR - About to send create request (simple):', {
        endpoint,
        method: 'POST',
        payload,
      });
      
      const response = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      console.log('🔍 ADDELEM_CREATE_ERROR - Response received:', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('🔍 ADDELEM_CREATE_ERROR - Error response:', errorData);
        throw new Error(errorData.error || 'Failed to create element');
      }
      
      const newItem = await response.json();
      console.log('🔍 ADDELEM_CREATE_ERROR - Item created successfully:', {
        newItemId: newItem.id,
        newItemType: newItem.type,
        newItemOrder: newItem.order,
      });
      
      setTimeout(() => {
        setCreating(false);
        onElementCreated({
          ...newItem,
          _refreshMeta: {
            parentComponentId,
            collectionId: collectionIdToInvalidate,
            pageId: target.pageId,
            heroId: target.heroId,
          }
        });
      }, 300);
      
    } catch (err) {
      console.error('🔍 ADDELEM_CREATE_ERROR - Failed to create simple element:', err);
      console.error('Failed to create element:', err);
      setError(err.message || 'Failed to create element. Please try again.');
      setCreating(false);
    }
  };

  // Handle template selection and item creation
  const handleTemplateSelect = async (template) => {
    if (!selectedElementType || !target) return;
    
    setCreating(true);
    setError(null);
    
    try {
      let endpoint;
      let payload = {
        type: selectedElementType,
        template_id: template.id,
      };
      
      let parentComponentId = null;
      let collectionIdToInvalidate = null;
      
      if (target.buttonCollectionId) {
        // Adding to ButtonCollection - must be a button
        // Two-step process: 1) Create button under component, 2) Add to collection
        if (selectedElementType !== 'button') {
          throw new Error('Only buttons can be added to button collections');
        }
        
        // Get collection's component
        const collectionRes = await apiFetch(`/api/items/button_collection/${target.buttonCollectionId}/`);
        if (!collectionRes.ok) {
          throw new Error('Failed to fetch button collection information');
        }
        const collection = await collectionRes.json();
        if (!collection.component) {
          throw new Error('ButtonCollection has no component');
        }
        
        parentComponentId = collection.component;
        collectionIdToInvalidate = target.buttonCollectionId;
        
        // Get component's current items to determine proper order
        const componentRes = await apiFetch(`/api/components/${collection.component}/items/`);
        if (!componentRes.ok) {
          throw new Error('Failed to fetch component items');
        }
        const componentItems = await componentRes.json();
        
        // Button should be added at END of component's items, not at the collection order
        payload.order = componentItems.length;
        endpoint = `/api/components/${collection.component}/items/`;
        
        // First, create the button under the component
        const createRes = await apiFetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        
        if (!createRes.ok) {
          const errorData = await createRes.json();
          throw new Error(errorData.error || 'Failed to create button');
        }
        
        const newButton = await createRes.json();
        
        // Second, add button to collection at specified order
        const addRes = await apiFetch(
          `/api/items/button_collection/${target.buttonCollectionId}/add-button/`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              button_id: newButton.id,
              order: target.order ?? 0,
            }),
          }
        );
        
        if (!addRes.ok) {
          throw new Error('Failed to add button to collection');
        }
        
        // Success - pass metadata for soft refresh
        setTimeout(() => {
          setCreating(false);
          onElementCreated({
            ...newButton,
            _refreshMeta: {
              parentComponentId,
              collectionId: collectionIdToInvalidate,
              pageId: target.pageId,
            }
          });
        }, 300);
        return;
        
      } else if (target.heroId) {
        // Adding to hero
        const heroRes = await apiFetch(`/api/items/hero/${target.heroId}/`);
        if (!heroRes.ok) {
          throw new Error('Failed to fetch hero information');
        }
        const hero = await heroRes.json();
        if (!hero.component) {
          throw new Error('Hero has no component');
        }
        parentComponentId = hero.component;
        endpoint = `/api/components/${hero.component}/items/`;
        payload.parent_hero_id = target.heroId;
        payload.order = target.order ?? 0;
        
      } else if (target.componentId) {
        // Adding to component - direct items only
        if (selectedElementType === 'component') {
          throw new Error('Components cannot be added inside another component');
        } else {
          // Regular item
          parentComponentId = target.componentId;
          endpoint = `/api/components/${target.componentId}/items/`;
          payload.order = target.order ?? 0;
          
          console.log('🔍 ADDELEM_CREATE_ERROR - Adding template item to component:', {
            componentId: target.componentId,
            elementType: selectedElementType,
            templateId: template.id,
            orderFromTarget: target.order,
            orderInPayload: payload.order,
            endpoint,
            fullPayload: payload,
          });
        }
        
      } else if (target.pageId) {
        // Adding to page - can be items OR components
        if (selectedElementType === 'component') {
          // Component creation at page level
          console.log('🔍 ADDELEM_CREATE_ERROR - Adding component template to page:', {
            pageId: target.pageId,
            websiteId,
            templateId: template.id,
            requestedPosition: target.order,
          });
          
          // Fetch page to get existing components for order calculation
          const pageResponse = await apiFetch(`/api/websites/${websiteId}/pages/id/${target.pageId}/`);
          if (!pageResponse.ok) {
            throw new Error('Failed to fetch page information');
          }
          const page = await pageResponse.json();
          const existingComponents = page.components || [];
          
          const requestedPosition = target.order ?? 0;
          
          // Calculate the optimal component order using fractional insertion
          const componentOrder = calculateComponentOrder(requestedPosition, existingComponents);
          
          console.log('🔍 ADDELEM_CREATE_ERROR - Calculated component order for component template:', {
            requestedPosition,
            existingComponentsCount: existingComponents.length,
            existingOrders: existingComponents.map(c => c.order ?? 0),
            calculatedComponentOrder: componentOrder,
          });
          
          endpoint = `/api/websites/${websiteId}/components/create/`;
          payload = {
            from_default: true,
            parent_website_page_id: target.pageId,
            order: componentOrder,
            template_id: template.id,
          };
        } else {
          // Item creation at page level - always create a new component so item appears directly on page
          console.log('🔍 ADDELEM_CREATE_ERROR - Adding template item to page:', {
            pageId: target.pageId,
            elementType: selectedElementType,
            templateId: template.id,
            requestedPosition: target.order,
          });
          
          // Step 1: Fetch page to get existing components for order calculation
          const pageResponse = await apiFetch(`/api/websites/${websiteId}/pages/id/${target.pageId}/`);
          if (!pageResponse.ok) {
            throw new Error('Failed to fetch page information');
          }
          const page = await pageResponse.json();
          const existingComponents = page.components || [];
          
          const requestedPosition = target.order ?? 0;
          
          // Step 2: Calculate the optimal component order using fractional insertion
          // This handles all cases: empty page, before first, after last, and between elements
          const componentOrder = calculateComponentOrder(requestedPosition, existingComponents);
          
          console.log('🔍 ADDELEM_CREATE_ERROR - Calculated component order for template item:', {
            requestedPosition,
            existingComponentsCount: existingComponents.length,
            existingOrders: existingComponents.map(c => c.order ?? 0),
            calculatedComponentOrder: componentOrder,
          });
          
          // Step 3: Create a new component at the calculated order
          // NOTE: Heroes are wrapped in a dedicated "blank" component so the wrapper
          // doesn't impose default padding/margins/background. The hero itself still
          // follows its template + website defaults.
          const isHeroWrapper = selectedElementType === 'hero';
          const componentPayload = {
            from_default: !isHeroWrapper,
            blank_for_hero: isHeroWrapper,
            parent_website_page_id: target.pageId,
            order: componentOrder,
          };
          
          const componentResponse = await apiFetch(`/api/websites/${websiteId}/components/create/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(componentPayload),
          });
          
          if (!componentResponse.ok) {
            const errorData = await componentResponse.json();
            throw new Error(errorData.error || 'Failed to create component');
          }
          
          const { component: newComponent } = await componentResponse.json();
          parentComponentId = newComponent.id;
          
          console.log('🔍 ADDELEM_CREATE_ERROR - Created new component for page template item:', {
            componentId: parentComponentId,
            componentOrder: newComponent.order,
            requestedPosition,
            calculatedOrder: componentOrder,
          });
          
          // Step 4: Add the item to the newly created component
          endpoint = `/api/components/${parentComponentId}/items/`;
          payload.order = 0; // First item in the new component
        }
        
      } else {
        throw new Error('No valid parent specified');
      }
      
      console.log('🔍 ADDELEM_CREATE_ERROR - About to send create request (template):', {
        endpoint,
        method: 'POST',
        payload,
      });
      
      const response = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      console.log('🔍 ADDELEM_CREATE_ERROR - Response received:', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('🔍 ADDELEM_CREATE_ERROR - Error response:', errorData);
        throw new Error(errorData.error || 'Failed to create element');
      }
      
      const newItem = await response.json();
      
      // Handle both item and component responses (components come wrapped in {component: {...}})
      const actualItem = newItem.component || newItem;
      const actualId = actualItem.id;
      const actualType = actualItem.type || selectedElementType;
      const actualOrder = actualItem.order;
      
      console.log('🔍 ADDELEM_CREATE_ERROR - Element created successfully (template):', {
        responseType: newItem.component ? 'component' : 'item',
        elementType: selectedElementType,
        requestedOrder: payload.order,
        receivedOrder: actualOrder,
        id: actualId,
        type: actualType,
        fullResponse: newItem,
      });

      setTimeout(() => {
        setCreating(false);
        onElementCreated({
          ...actualItem,
          _refreshMeta: {
            parentComponentId,
            collectionId: collectionIdToInvalidate,
            pageId: target.pageId,
            heroId: target.heroId,
          }
        });
      }, 300);
      
    } catch (err) {
      console.error('🔍 ADDELEM_CREATE_ERROR - Failed to create template element:', err);
      console.error('Failed to create element:', err);
      setError(err.message || 'Failed to create element. Please try again.');
      setCreating(false);
    }
  };

  return (
    <motion.div 
      ref={panelRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.2 }}
      data-tutorial-id={TUTORIAL_TARGETS.ADD_ELEMENT_PANEL}
      className={`h-full flex flex-col ${surfaces.primary} relative`}
    >
      {/* Header */}
      <div className={`p-4 border-b ${borders.default} flex items-center justify-between flex-shrink-0 bg-transparent`}>
        <h2 className={`${typography.h4} ${textColors.primary}`}>Add Element</h2>
        <button 
          onClick={onCancel} 
          className={`${textColors.tertiary} ${textColors.hover} p-1 rounded ${surfaces.hover} transition-colors`}
          aria-label="Close"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="mx-4 mt-4 p-4 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <div className={`${typography.body} text-red-800 dark:text-red-300 font-medium mb-1`}>Error</div>
          <div className={`${typography.bodySmall} text-red-600 dark:text-red-400 mb-3`}>{error}</div>
          {TEMPLATE_ELEMENTS.includes(selectedElementType) && (
            <SecondaryButton
              onClick={() => fetchTemplates(selectedElementType)}
              variant="danger"
              className={typography.bodySmall}
            >
              Retry
            </SecondaryButton>
          )}
        </div>
      )}
      
      {/* Two-column layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left column - element types */}
        <div 
          className={`overflow-y-auto ${surfaces.secondary} transition-all duration-300 ${
            shouldHideRightColumn 
              ? 'w-full p-4' // Full width when narrow and nothing selected
              : isCompact 
                ? shouldExpandOnHover
                  ? `w-64 p-4 border-r ${borders.default}` // Expanded on hover
                  : `w-16 p-2 border-r ${borders.default}` // Icon-only when narrow and something selected
                : `w-64 p-4 border-r ${borders.default}` // Normal width when wide
          }`}
          onMouseEnter={() => setIsSidebarHovered(true)}
          onMouseLeave={() => setIsSidebarHovered(false)}
        >
          <ElementTypesList
            selectedType={selectedElementType}
            onSelectType={setSelectedElementType}
            isCompact={isCompact}
            showLabelsOnHover={shouldExpandOnHover}
            allowedTypes={availableElementTypes}
          />
        </div>
        
        {/* Right column - templates (hidden when narrow and nothing selected, or when simple element selected) */}
        {!shouldHideRightColumn && TEMPLATE_ELEMENTS.includes(selectedElementType) && (
          <div className={`flex-1 overflow-y-auto p-4 ${surfaces.primary}`}>
            <TemplatesList
              elementType={selectedElementType}
              templates={templates}
              onSelectTemplate={handleTemplateSelect}
              isLoading={loadingTemplates}
            />
          </div>
        )}
      </div>
      
      {/* Loading overlay.
          We also paint this overlay when ``autoCreateType`` is set and no
          error has surfaced yet: the creation dispatch happens across two
          effects (seed selected type → run handler) so without this we'd
          briefly render the element picker before the spinner. If auto-
          creation fails, ``error`` is set and we drop back to the normal
          picker so the user can retry manually. */}
      {(creating || (autoCreateType && !error)) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`absolute inset-0 ${surfaces.primary} bg-opacity-90 dark:bg-opacity-95 flex items-center justify-center z-50`}
        >
          <div className="flex flex-col items-center">
            <LoadingSpinner size="3xl" className="mb-3" />
            <div className={`${typography.body} ${textColors.secondary} font-medium`}>Creating element...</div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default AddElementPanel;
